import React, { useState } from 'react';
import { Grid, Typography, TextField, Button, Paper, Box, Alert, CircularProgress } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import { submitHelpRequest } from '../providers/feedbackApi';
import { IHelpRequest } from '../models/iHelpRequest';
import { toast } from 'react-toastify';
import { useLogger } from '../hooks/useLogger';

const HelpPage = () => {
  const location = useLocation();
  const { authState } = useOktaAuth();
  const sourceApp = location.state?.from || 'ESA';
  const logger = useLogger('HelpPage');

  const [formData, setFormData] = useState<IHelpRequest>({
    subject: '',
    description: '',
    applicationContext: sourceApp,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    } else if (formData.subject.trim().length > 200) {
      newErrors.subject = 'Subject must be no more than 200 characters';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (formData.description.trim().length < 10) {
      newErrors.description = 'Description must be at least 10 characters';
    } else if (formData.description.trim().length > 2000) {
      newErrors.description = 'Description must be no more than 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const accessToken = authState?.accessToken?.accessToken;
    if (!validateForm() || !accessToken) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await submitHelpRequest(accessToken, {
        ...formData,
        subject: formData.subject.trim(),
        description: formData.description.trim(),
      });

      toast.success(`Help request submitted successfully! Request Id: ${response.ticketKey}`);

      setFormData({
        subject: '',
        description: '',
        applicationContext: sourceApp,
      });
      setErrors({});
    } catch (error: any) {
      logger.UI.error('Failed to submit help request', error);

      if (error.response?.status === 400) {
        toast.error('Please check your input and try again');
      } else {
        toast.error('Failed to submit help request. Please try again later.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange =
    (field: keyof IHelpRequest) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData({
        ...formData,
        [field]: event.target.value,
      });

      if (errors[field]) {
        setErrors({
          ...errors,
          [field]: '',
        });
      }
    };

  const accessToken = authState?.accessToken?.accessToken;

  return (
    <Grid container direction="row" wrap="wrap" justifyContent="center" spacing={4} padding={2}>
      <Grid
        size={{
          xs: 12,
          md: 8,
          lg: 6
        }}>
        <Paper elevation={3} sx={{ padding: 4 }}>
          <Typography variant="h4" gutterBottom>
            {`Need Help with ${sourceApp}?`}
          </Typography>

          <Typography variant="body1" paragraph>
            {`Have a question or problem? Fill out the form below to send a request to the team responsible for
            ${sourceApp}. The more detail you provide, the faster we can help you.`}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  placeholder="Brief summary of your issue or question"
                  value={formData.subject}
                  onChange={handleInputChange('subject')}
                  error={!!errors.subject}
                  helperText={errors.subject || `${formData.subject.length}/200 characters`}
                  required
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  label="Description"
                  placeholder="Describe the issue in detail. What did you do, what did you expect, and what happened instead? Please include any error messages."
                  value={formData.description}
                  onChange={handleInputChange('description')}
                  error={!!errors.description}
                  helperText={errors.description || `${formData.description.length}/2000 characters`}
                  required
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    disabled={isSubmitting}
                    onClick={() => {
                      setFormData({
                        subject: '',
                        description: '',
                        applicationContext: sourceApp,
                      });
                      setErrors({});
                    }}
                  >
                    Clear
                  </Button>

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting || !accessToken}
                    startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                  >
                    {isSubmitting ? 'Submitting request...' : 'Submit Request'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>

          {!accessToken && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              You must be logged in to submit a request.
            </Alert>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
};

export default HelpPage;
